import { useMutation, useQueryClient } from 'react-query';

import type { IStandardErrorResponse } from 'src/api/common';
import type { IUser } from 'src/api/users-api';
import Auth from 'src/services/api/auth/Auth';
import { simpleKy } from 'src/services/api/ky';
import { apiClient } from 'src/services/api/client';
import { queryKeys } from 'src/services/api/constants';
import { useAssociateEmailAddress } from 'src/services/api/onboarding/use-associate-email-address';

const PATH = 'api/landlord/onboard/user-profile';

type ApiResponse = IUser;

type Request = {
    code: string;
    name: string;
    email: string;
    phone_number: string;
    password: string;
}

/**
 * If the user is already logged in (e.g. resuming onboarding in this browser),
 * we will update the user's profile. Otherwise we will create a new user.
 */
export const useCreateOrUpdateOnboardingUser = () => {
    const queryClient = useQueryClient();

    const { mutateAsync: associateEmailAddress } = useAssociateEmailAddress();

    return useMutation<ApiResponse, IStandardErrorResponse, Request>(
        async ({ code, email, ...profile}) => {
            if (Auth.accessToken) {
                await apiClient(PATH, {
                    method: 'patch',
                    json: {
                        profile
                    },
                });
            } else {
                await simpleKy(PATH, {
                    method: 'post',
                    json: {
                        code,
                        profile
                    },
                });
            }

            const res = await Auth.login(email, profile.password);
            const loggedInUser = res.user;

            // note that the team ingestion email may be present earlier
            if (!loggedInUser.ingest_emails || !loggedInUser.ingest_emails.individual) {
                await associateEmailAddress();
            }

            queryClient.setQueryData(queryKeys.loggedInUser, loggedInUser);

            return loggedInUser;
        }, {
            onSuccess: () => {
                queryClient.invalidateQueries([queryKeys.loggedInUser]);
            }
        }
    );
};
